import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { Modal } from 'jl-design-system/components/modal/Modal';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import { PrimaryLink, SecondaryLink } from 'jl-design-system/elements/link/Link';
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import batchingFailureModalState from './BatchingFailureModal.state';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { useHandbackToUrl } from '@hooks/useHandbackToUrl';
import { removeBatchingFailureItems } from '@redux/actions/edit-basket/editBasketActions';
import { disabledEscKeyHandler } from '@utils/helpers/eventKeyActions';
import { getItemQuantityPhrase, getItemStatePhrase, pluraliseItemWord } from '@utils/helpers/pluraliseItem';

// Components
import Container from '@components/container';
import OrderItem from '@components/order-item';

// Styles
import styles from './batching-failure-modal.scss';

export const BatchingFailureModal = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    allItemsHaveBatchingFailure,
    basketUrl,
    batchingFailureItems = [],
    removeItemsFailed,
    showModal,
  } = useSelector(batchingFailureModalState);
  const { isDesktop } = useWindowSize();
  const handbackProps = useHandbackToUrl(basketUrl);

  useEffect(() => {
    document.addEventListener('keydown', disabledEscKeyHandler, true);

    return () => {
      document.removeEventListener('keydown', disabledEscKeyHandler, true);
    };
  }, []);

  if (!showModal) {
    return null;
  }

  const singleItem = batchingFailureItems.length === 1;
  const removeItemButtonText = `Remove ${pluraliseItemWord(singleItem)} and continue`;
  const topText = `We're sorry but the below ${getItemStatePhrase(singleItem)} unavailable to buy online.`;

  const handleRemoveButtonClick = () => dispatch(removeBatchingFailureItems(batchingFailureItems));

  const modalContent =
    batchingFailureItems.length === 0 ? (
      <Container
        flex={{
          justifyContent: 'center',
        }}
        margin="2"
      >
        <LoadingSpinner />
      </Container>
    ) : (
      <>
        <p data-testid="batching-failure-top-text">{topText}</p>

        <Container borderBottom="grey-15" borderTop="grey-15" marginY="3">
          {batchingFailureItems.map((item) => (
            <OrderItem {...item} key={item.id} displayImage hideSubtotals />
          ))}
        </Container>

        {(allItemsHaveBatchingFailure || removeItemsFailed) && (
          <p data-testid="batching-failure-bottom-text">
            Please return to the basket. We apologise for any inconvenience caused.
          </p>
        )}

        {!allItemsHaveBatchingFailure && !removeItemsFailed && (
          <Container testId="batching-failure-bottom-text">
            <p>
              Please remove {getItemQuantityPhrase(singleItem)} to continue with the rest of your order or you can
              return to the basket.
            </p>
            <p>We apologise for any inconvenience caused.</p>
          </Container>
        )}

        {removeItemsFailed && (
          <Container marginBottom="2">
            <Message
              title={`Sorry, we couldn't remove the ${pluraliseItemWord(singleItem)}. Please return to basket.`}
              type="error"
            />
          </Container>
        )}

        <Container
          flex={{
            alignItems: 'center',
            flexDirection: isDesktop ? 'row' : 'col-reverse',
            flexWrap: 'wrap',
            justifyContent: isDesktop ? 'end' : 'start',
          }}
          marginTop="3"
        >
          {(allItemsHaveBatchingFailure || removeItemsFailed) && (
            <PrimaryLink
              data-testid="batching-failure-return-to-basket-primary"
              external
              inline={isDesktop}
              {...handbackProps}
            >
              Return to basket
            </PrimaryLink>
          )}

          {!allItemsHaveBatchingFailure && !removeItemsFailed && (
            <>
              <SecondaryLink
                data-testid="batching-failure-return-to-basket-secondary"
                external
                inline={isDesktop}
                {...handbackProps}
              >
                Return to basket
              </SecondaryLink>

              <PrimaryButton
                data-testid="batching-failure-remove-item"
                inline={isDesktop}
                onClick={handleRemoveButtonClick}
              >
                {removeItemButtonText}
              </PrimaryButton>
            </>
          )}
        </Container>
      </>
    );

  return (
    <Modal className={styles.modal} footer={false} isOpen title="Something's gone wrong">
      {modalContent}
    </Modal>
  );
};

export default BatchingFailureModal;
